import { useL } from "@/lib/useL";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { MapPin, ShieldCheck, Clock, MessageCircle, Search, RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import PageSEO from "@/components/PageSEO";
import LatestGuides from "@/components/LatestGuides";
import FareTable, { type Fare } from "@/components/pricing/FareTable";
import { saveFaresCache, loadFaresCache } from "@/lib/faresCache";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[-/]/g, " ");

const ZONE_ORDER = ["Marrakech", "Essaouira", "Agadir", "Casablanca"];

const Tarifs = () => {
  const L = useL();
  const [fares, setFares] = useState<Fare[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [departure, setDeparture] = useState("");
  const [arrival, setArrival] = useState("");
  const firstMatchRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    (async () => {
      try {
        const { data, error } = await supabase
          .from("transfer_fares")
          .select(
            "id, zone, from_location, to_location, price_1_3, price_4_7, price_8_17, currency, is_new"
          )
          .eq("is_active", true)
          .order("position", { ascending: true })
          .abortSignal(AbortSignal.timeout(10000));
        if (error) throw error;
        const list = (data as Fare[]) || [];
        setFares(list);
        saveFaresCache(list);
      } catch {
        const cached = loadFaresCache<Fare>();
        if (cached && cached.fares.length > 0) {
          setFares(cached.fares);
          setFromCache(true);
        } else {
          setLoadError(true);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredFares = fares.filter((f) => {
    const from = normalize(f.from_location);
    const to = normalize(f.to_location);
    const dep = normalize(departure).trim();
    const arr = normalize(arrival).trim();
    const depMatch = !dep || from.includes(dep);
    const arrMatch = !arr || to.includes(arr);
    return depMatch && arrMatch;
  });

  const zones = [
    ...ZONE_ORDER.filter((z) => filteredFares.some((f) => f.zone === z)),
    ...Array.from(new Set(filteredFares.map((f) => f.zone))).filter((z) => !ZONE_ORDER.includes(z)),
  ];

  useEffect(() => {
    if (!departure.trim() && !arrival.trim()) return;
    const firstZone = zones[0];
    if (!firstZone) return;
    const el = document.getElementById(`zone-${firstZone}`);
    if (el && el !== firstMatchRef.current) {
      firstMatchRef.current = el;
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [departure, arrival, zones]);

  const offers = fares
    .filter((f) => f.price_1_3)
    .map((f) => ({
      "@type": "Offer",
      name: `Private transfer ${f.from_location} → ${f.to_location}`,
      priceCurrency: f.currency,
      price: f.price_1_3,
      availability: "https://schema.org/InStock",
    }));

  return (
    <main className="min-h-screen bg-background">
      <PageSEO
        title={L("Morocco Private Transfer Prices — Fixed Rates 2026", "Tarifs transferts privés Maroc — Prix fixes 2026")}
        description={L("Full price list for private transfers in Morocco: Marrakech, Essaouira, Agadir, Casablanca, Merzouga. Fixed prices per vehicle, taxes included.", "Grille tarifaire complète des transferts privés au Maroc : Marrakech, Essaouira, Agadir, Casablanca, Merzouga. Prix fixes par véhicule, taxes incluses.")}
        path="/tarifs"
      />
      <Helmet>
        <script type="application/ld+json" id="tarifs-jsonld">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Private transfer and driver in Morocco",
            provider: {
              "@type": "LocalBusiness",
              name: "Maroctransfers",
              url: "https://maroctransfers.com",
            },
            areaServed: "MA",
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "Morocco transfer prices",
              itemListElement: offers,
            },
          })}
        </script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <header className="pt-28 pb-14 bg-gradient-to-br from-primary/10 via-accent/5 to-background border-b border-border">
        <div className="container mx-auto px-6 max-w-6xl">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-muted-foreground mb-5">
            <Link to="/" className="hover:text-primary">{L('Home', 'Accueil')}</Link>
            <span>›</span>
            <span className="text-foreground font-medium">{L('Prices', 'Tarifs')}</span>
          </nav>
          <p className="font-body text-xs md:text-sm uppercase tracking-[0.24em] text-primary font-bold mb-3">
            {L('Prices & Services', 'Tarifs & Prestations')}
          </p>
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground max-w-3xl">
            {L('Our private transfer prices in Morocco', 'Tarifs de nos transferts privés au Maroc')}
          </h1>
          <p className="mt-4 font-body text-base md:text-lg text-muted-foreground max-w-2xl">
            {L('Fixed prices, taxes and tolls included, private multilingual driver. Choose your route and number of passengers: the price shown is the price you pay.', 'Prix fixes, taxes et péages inclus, chauffeur privé francophone. Choisissez votre trajet et le nombre de passagers : le tarif affiché est le tarif payé.')}
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm font-body text-muted-foreground">
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> {L('Guaranteed fixed prices', 'Prix fixes garantis')}</span>
            <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> {L('Available 24/7', 'Disponible 24h/24')}</span>
            <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {L('All of Morocco', 'Tout le Maroc')}</span>
          </div>
          <div className="mt-7">
            <Button
              asChild
              size="lg"
              className="bg-[#25D366] hover:bg-[#1ebe5a] text-white font-body font-bold rounded-2xl"
            >
              <a
                href={buildWhatsAppUrl(
                  "Hello Maroctransfers, I would like a quote for a private transfer in Morocco."
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                {L('Get a WhatsApp quote', 'Demander un devis WhatsApp')}
              </a>
            </Button>
          </div>
        </div>
      </header>

      {/* Recherche de trajets */}
      <div className="py-10 border-b border-border">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1 space-y-2">
                <label htmlFor="departure" className="font-body text-sm font-semibold text-foreground">
                  {L('Departure', 'Départ')}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="departure"
                    type="text"
                    placeholder="Ex. Marrakech"
                    value={departure}
                    onChange={(e) => setDeparture(e.target.value)}
                    className="pl-9 font-body"
                  />
                </div>
              </div>
              <div className="flex-1 space-y-2">
                <label htmlFor="arrival" className="font-body text-sm font-semibold text-foreground">
                  {L('Arrival / Destination', 'Arrivée / Destination')}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="arrival"
                    type="text"
                    placeholder="Ex. Essaouira"
                    value={arrival}
                    onChange={(e) => setArrival(e.target.value)}
                    className="pl-9 font-body"
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDeparture("");
                  setArrival("");
                  firstMatchRef.current = null;
                }}
                className="font-body font-semibold border-primary/40 text-primary hover:bg-primary/10"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                {L('Reset', 'Réinitialiser')}
              </Button>
            </div>
            {(departure.trim() || arrival.trim()) && (
              <p className="mt-4 text-sm font-body text-muted-foreground">
                {filteredFares.length} {L(filteredFares.length > 1 ? "routes found" : "route found", filteredFares.length > 1 ? "trajets trouvés" : "trajet trouvé")}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Grilles tarifaires */}
      <div className="py-14">
        <div className="container mx-auto px-6 max-w-6xl space-y-14">
          {fromCache && (
            <p className="rounded-xl border border-border bg-card px-4 py-3 text-center font-body text-sm text-muted-foreground">
              {L('Prices shown from the last saved version — final confirmation via WhatsApp.', 'Tarifs affichés depuis la dernière version enregistrée — la confirmation finale se fait par WhatsApp.')}
            </p>
          )}
          {loading ? (
            <p className="text-center font-body text-muted-foreground">{L('Loading prices…', 'Chargement des tarifs…')}</p>
          ) : loadError ? (
            <div className="text-center py-12 rounded-2xl border border-border bg-card p-8">
              <p className="font-heading text-xl font-bold text-foreground mb-2">
                {L('Prices are temporarily unavailable', 'Les tarifs sont momentanément indisponibles')}
              </p>
              <p className="font-body text-muted-foreground mb-6">
                {L('Message us on WhatsApp: we will give you your price within minutes.', 'Écrivez-nous sur WhatsApp : nous vous donnons le prix de votre trajet en quelques minutes.')}
              </p>
              <Button asChild>
                <a href={buildWhatsAppUrl("Hello, I would like to know the price of a transfer.")} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-4 w-4" /> {L('Ask for a price on WhatsApp', 'Demander un prix sur WhatsApp')}
                </a>
              </Button>
            </div>
          ) : zones.length === 0 ? (
            <div className="text-center py-12 rounded-2xl border border-border bg-card p-8">
              <p className="font-heading text-xl font-bold text-foreground mb-2">
                {L('No route found for these cities', 'Aucun trajet trouvé pour ces villes')}
              </p>
              <p className="font-body text-muted-foreground mb-6">
                {L('Request a custom quote for your itinerary.', 'Demandez un devis personnalisé pour votre itinéraire.')}
              </p>
              <Button
                asChild
                size="lg"
                className="bg-[#25D366] hover:bg-[#1ebe5a] text-white font-body font-bold rounded-2xl"
              >
                <a
                  href={buildWhatsAppUrl(
                    `Hello Maroctransfers, I would like a quote for a transfer${departure ? ` from ${departure}` : ""}${arrival ? ` to ${arrival}` : ""}.`
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  {L('Quote on WhatsApp', 'Devis sur WhatsApp')}
                </a>
              </Button>
            </div>
          ) : (
            zones.map((zone) => (
              <section key={zone} id={`zone-${zone}`} aria-labelledby={`zone-title-${zone}`}>
                <h2
                  id={`zone-title-${zone}`}
                  className="font-heading text-2xl md:text-3xl font-bold text-primary mb-2"
                >
                  {L("Transfers from", "Transferts depuis")} {zone}
                </h2>
                <h3 className="font-body text-sm text-muted-foreground mb-5">
                  {L('Price per vehicle by number of passengers (1-3, 4-7 or 8-17 seats).', 'Prix par véhicule selon le nombre de passagers (1-3, 4-7 ou 8-17 places).')}
                </h3>
                <FareTable
                  fares={filteredFares.filter((f) => f.zone === zone)}
                  highlightFrom={departure}
                  highlightTo={arrival}
                />
              </section>
            ))
          )}

          <p className="text-center font-body text-xs text-muted-foreground">
            {L('Indicative one-way prices. Final confirmation via WhatsApp or the booking form.', 'Tarifs indicatifs, aller simple. Confirmation finale par WhatsApp ou via le formulaire de réservation.')}
          </p>
        </div>
      </div>

      <LatestGuides
        limit={6}
        title={L("Our latest travel guides", "Nos derniers guides de voyage")}
        subtitle={L("Tips, itineraries and insider advice to plan your trip to Morocco.", "Conseils, itinéraires et bons plans pour organiser votre séjour au Maroc.")}
      />

      <Footer />
      <WhatsAppButton />
    </main>
  );
};

export default Tarifs;
